import moment from "moment-jalaali";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useOrderForm } from "../../../hooks/useOrderForm";
import { useResponsive } from "../../../hooks/useResponsive";
import { Sidebar } from "../../../layouts/Sidebar";
import { setLoading } from "../../../services/reducers/homeSlice";
import { useAppSelector } from "../../../services/store";
import { User } from "../../../types/types";
import { initialFormState } from "../../../utils/constants";
import { orderService } from "./OrderService";
import AddressSection from "./Address";
import BillSummarySection from "./BillSummary";
import OrderCompletionSection from "./OrderCompletion";
import OrderInfoSection from "./OrderInfo";
import PaymentInfoSection from "./PaymentInfo";
import ServicesSection from "./Services";
import StylistSection from "./Stylist";
import UserInfoSection from "./UserInfo";
import UserUploadedPhoto from "./UserUploadedPhoto";
import PaymentMethods = globalEnum.PaymentMethods;
import globalEnum from "enums/globalEnum";

const OrderManage: React.FC = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const serviceReducer = useAppSelector((state) => state.serviceReducer);
  const [workers, setWorkers] = useState<User[]>([]);
  const [colors, setColors] = useState<any[]>([]);
  const [copyOf, setCopyOf] = useState("");
  const { isMobile, isTablet, sidebarOpen, setSidebarOpen } = useResponsive();

  const {
    form,
    setForm,
    address,
    setAddress,
    updateFormField,
    updateNestedField,
    isColored,
    calculatePrices,
    setUserBalance,
    urgentAllPrices,
  } = useOrderForm(initialFormState, serviceReducer);

  const isEdit = !!id;

  const handleSubmit = async () => {
    dispatch(setLoading(true));

    try {
      const result = await orderService.saveOrder(form, id);

      if (!result.success) {
        throw new Error();
      }

      Swal.fire({
        title: "موفق",
        text: "سفارش با موفقیت ثبت شد",
        icon: "success",
        confirmButtonText: "متوجه شدم",
        didClose() {
          navigate(-1);
        },
      });
    } catch (error) {
      Swal.fire({
        title: "ناموفق",
        text: "خطا در ارتباط با سرور",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const fetchOrderData = async () => {
    const orderId = id || copyOf;
    if (!orderId) {
      return;
    }

    dispatch(setLoading(true));
    try {
      const result = await orderService.fetchOrder(orderId);
      if (!result.success) {
        throw new Error();
      }

      if (id) {
        setForm(result.data);
        setAddress(result.data.address);
        setUserBalance(result.data.user.walletBalance);
        if (result.data.worker) {
          setWorkers([result.data.worker]);
        }
      } else {
        setAddress(result.data.address);
        setUserBalance(result.data.user.walletBalance);
        setForm((prev) => ({
          ...prev,
          price: result.data?.price,
          serviceId: result.data?.serviceId,
          transportation: result.data?.transportation,
          isUrgent: result.data?.isUrgent,
          finalPrice: result.data?.finalPrice,
          time: result.data?.time,
          toTime: result.data?.toTime,
          date: moment().add(1, "d").format("jYYYY/jMM/jDD"),
          payment: {
            method: PaymentMethods.card,
            finalPrice: result.data?.finalPrice,
            shouldUseWallet: false,
            isPaid: false,
          },
          orderServices: result.data?.orderServices,
          address: result.data?.address,
          addressId: result.data?.addressId,
          user: result.data?.user,
          userId: result.data?.userId,
        }));
      }
    } catch (error) {
      Swal.fire({
        title: "خطا",
        text: "خطا در ارتباط با سرور",
        icon: "error",
        confirmButtonText: "متوجه شدم",
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      dispatch(setLoading(true));
      try {
        const res = await Promise.all([
          orderService.fetchColors(),
          fetchOrderData(),
        ]);
        setColors(res[0].data);
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchInitialData();
  }, [copyOf, id]);

  useEffect(() => {
    calculatePrices();
  }, [
    form.orderServices,
    form.transportation,
    form.discountAmount,
    form.isUrgent,
    form.payment?.shouldUseWallet,
  ]);

  useEffect(() => {
    if (!isEdit) {
      document?.getElementById("phoneNumber")?.focus();
    }
    const copyOfQuery = searchParams.get("copyOf");
    if (copyOfQuery) {
      setCopyOf(copyOfQuery);
    }
  }, [searchParams]);

  return (
    <div className="dashboardBody">
      {/* Mobile Menu Toggle */}
      {isMobile && (
        <button
          className="mobile-menu-toggle"
          onClick={toggleSidebar}
          aria-label="منوی کناری"
          aria-expanded={sidebarOpen}
        >
          ☰
        </button>
      )}

      {/* Sidebar Backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className={`sidebar-backdrop ${sidebarOpen ? "show" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        className={`sideBarContainer ${sidebarOpen ? "mobile-open" : ""}`}
      />

      <main className="dashBoardMain main">
        <div className={`addInfoHeader ${isMobile ? "mobile-header" : ""}`}>
          <button
            className="dashboardHeader keepRight"
            onClick={handleSubmit}
            aria-label="ثبت سفارش"
          >
            <p>ثبت سفارش</p>
          </button>

          <span className={isMobile ? "mobile-title-section" : ""}>
            <h1 className="sideBarTitle">
              {!isMobile && "بازگشت به صفحه لیست سفارش ها"}
            </h1>
            <h1 className="dashBoardTitle">
              {isEdit ? "ویرایش" : "ایجاد"} سفارش
            </h1>
          </span>

          <i
            className="backAdd clickable"
            onClick={() => navigate(-1)}
            role="button"
            aria-label="بازگشت"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate(-1)}
          ></i>
        </div>

        <section
          className={`topRow ${isMobile ? "mobile-sections" : isTablet ? "tablet-sections" : ""}`}
        >
          <UserInfoSection
            form={form}
            updateNestedField={updateNestedField}
            fetchUser={orderService.fetchUser}
          />
          <OrderInfoSection
            form={form}
            updateFormField={updateFormField}
            urgentAllPrices={urgentAllPrices}
            updateNestedField={updateNestedField}
          />
          <BillSummarySection form={form} updateFormField={updateFormField} />
          <StylistSection
            form={form}
            updateFormField={updateFormField}
            workers={workers}
            setWorkers={setWorkers}
            getWorkers={orderService.getWorkers}
            orderId={id}
          />
          <PaymentInfoSection
            form={form}
            updateNestedField={updateNestedField}
            id={id}
          />
          <AddressSection
            form={form}
            address={address}
            setAddress={setAddress}
            updateNestedField={updateNestedField}
          />
          <ServicesSection
            form={form}
            setForm={setForm}
            serviceReducer={serviceReducer}
            isColored={isColored}
            colors={colors}
          />
          <OrderCompletionSection form={form} />
          <UserUploadedPhoto form={form} />
        </section>
      </main>
    </div>
  );
};

export default OrderManage;
